import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ClockIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import Helper from "../helpers";
import config from "../config";

const baseUrl = config.apiBaseUrl;

// Payment outcome types returned from the API
const OUTCOME = {
    TOKENS:       'tokens',
    CLASS:        'class',
    SUBSCRIPTION: 'subscription',
    UNKNOWN:      'unknown',
};

const PaymentComplete = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [outcome, setOutcome] = useState(OUTCOME.UNKNOWN);
    const [tokensApplied, setTokensApplied] = useState(null);
    const [venueKey, setVenueKey] = useState('');
    const [apiMessage, setApiMessage] = useState('');

    // Get URL parameters to determine if this is a guest user
    const params = new URLSearchParams(location?.search || '');
    const isGuest = params.get('guest') === 'guest';

    useEffect(() => {
        const handlePaymentCompletion = async () => {
            const params = new URLSearchParams(location.search);
            const step = params.get('step');
            const ogt = params.get('ogt');
            //const guest = params.get('guest');
            const activity = params.get('type');
            
            // NEW: Handle GoCardless redirect from signup flow
            const billingRequestId = params.get('billing_request');
            const redirectFlowId = params.get('redirect_flow_id');
            const venueKey = params.get('venue_key');
            const mode = params.get('mode');
            const userId = params.get('user_id');
            const subscriptionId = params.get('subscription_id');
            const inviteCode = params.get('invite_id');  // NEW: Extract invite code
            let orderId = params.get('order_id');  // NEW: Extract invite code
            
            if(!orderId){
                orderId = params.get('orderId');
            }

            // NEW: Signup flow - proxy to completepayment or completevenuepayment endpoint
            if (billingRequestId || redirectFlowId) {
                try {
                    // Determine endpoint based on mode
                    const endpoint = mode === 'venue-admin' 
                        ? `${baseUrl}/api/PaymentAdmin/completevenuepayment`
                        : `${baseUrl}/api/PaymentAdmin/completepayment`;
                    
                    const requestBody = mode === 'venue-admin'
                        ? {
                            venueKey: venueKey,
                            billingRequestId: billingRequestId,
                            orderId: orderId,
                            subscriptionId: subscriptionId
                          }
                        : {
                            venueKey: venueKey,
                            orderId: orderId,
                            billingRequestId: billingRequestId,
                            redirectFlowId: redirectFlowId,
                            mode: mode,
                            userId: userId,
                            subscriptionId: subscriptionId,
                            inviteCode: inviteCode
                          };
                    
                    const response = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(requestBody),
                        credentials: 'include'
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setSuccess(true);
                        
                        // Redirect based on mode
                        setTimeout(() => {
                            if (mode === 'venue-admin') {
                                navigate('/venue-activation', { 
                                    state: { 
                                        venueKey: data.venueKey || venueKey,
                                        venueName: data.venueName,
                                        fromPayment: true 
                                    } 
                                });
                            } else {
                                navigate('/signup-complete', { 
                                    state: { 
                                        venueKey: data.venueKey || venueKey,
                                        venueName: data.venueName,
                                        subscriptionId: data.subscriptionId,
                                        fromPayment: true,
                                        mode: mode 
                                    } 
                                });
                            }
                        }, 3000);
                    } else {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Failed to complete payment');
                    }
                } catch (error) {
                    console.error('Error completing signup payment:', error);
                    setError(error.message || 'Failed to complete payment processing');
                }
                setLoading(false);
                return;
            }

            // EXISTING: Original payment completion flow
            if (!orderId) {
                setError('No order ID found in the callback URL');
                setLoading(false);
                return;
            }

            if (step === 'complete' && ogt === 'payment') {
                try {
                    const token = await Helper.getCookie('wh-jwt');
                    
                    const response = await fetch(`${baseUrl}/api/order/user/complete`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            OrderId: orderId
                        }),
                        credentials: 'include'
                    });

                    if (response.ok) {
                        const data = await response.json();

                        setVenueKey(data.venueKey || '');
                        setApiMessage(data.message || '');
                        setSuccess(true);

                        // Determine outcome type from the response
                        if (data.tokensPending != null) {
                            // Token purchase — tokens will be credited by the GC webhook on confirmation
                            setOutcome(OUTCOME.TOKENS);
                            setTokensApplied(data.tokensPending);
                            setTimeout(() => navigate('/dashboard/manage-subscription'), 5000);

                        } else if (data.message && data.message.toLowerCase().includes('booking')) {
                            // Class / pay-in-advance session — webhook will confirm
                            setOutcome(OUTCOME.CLASS);
                            const redirectDelay = isGuest ? 11000 : 5000;
                            setTimeout(() => {
                                if (isGuest) {
                                    switch (activity) {
                                        case 'classes': navigate(`/classes/${data.venueKey}`); break;
                                        case 'shop':    navigate('/shop'); break;
                                        default:        navigate('/');
                                    }
                                } else {
                                    navigate(data.venueKey ? `/dashboard/classes` : '/dashboard');
                                }
                            }, redirectDelay);

                        } else {
                            // Subscription / mandate / fallback
                            setOutcome(OUTCOME.SUBSCRIPTION);
                            setTimeout(() => navigate('/dashboard/manage-subscription'), 3000);
                        }

                    } else {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Failed to complete payment');
                    }
                } catch (error) {
                    console.error('Error completing payment:', error);
                    setError(error.message || 'Failed to complete payment processing');
                }
            } else {
                setError('Invalid payment completion callback');
            }

            setLoading(false);
        };

        handlePaymentCompletion();
    }, [location, navigate, isGuest]);

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                    <p className="text-white text-lg">Processing your payment...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    {success ? (
                        <>
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 mb-6">
                                <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2">
                                {isGuest ? 'Thank You!' : 'Payment Successful!'}
                            </h2>
                            <p className="text-gray-400 mb-6">
                                {isGuest
                                    ? 'We are processing your payment and you will receive an email confirmation once complete. Thank you for your booking!'
                                    : 'Your payment has been processed successfully.'}
                            </p>

                            {/* ── Token purchase outcome ─────────────────────────── */}
                            {outcome === OUTCOME.TOKENS && tokensApplied != null && !isGuest && (
                                <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4 mb-4 flex items-center gap-3">
                                    <ClockIcon className="h-8 w-8 text-blue-400 flex-shrink-0" />
                                    <div className="text-left">
                                        <p className="text-blue-400 text-sm">
                                            Your tokens will be added to your account once your payment has been confirmed by GoCardless. You will receive an email when they are ready to use.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* ── Class / session booking pending outcome ───────── */}
                            {outcome === OUTCOME.CLASS && (
                                <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4 mb-4 flex items-center gap-3">
                                    <ClockIcon className="h-8 w-8 text-blue-400 flex-shrink-0" />
                                    <div className="text-left">
                                        <p className="text-blue-300 font-semibold">
                                            Booking pending payment confirmation
                                        </p>
                                        <p className="text-blue-400 text-sm">
                                            {apiMessage || 'Your booking is being confirmed — you will receive an email once payment has been verified.'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* ── Subscription / mandate outcome ────────────────── */}
                            {outcome === OUTCOME.SUBSCRIPTION && (
                                <div className="bg-emerald-900/30 border border-emerald-600 rounded-lg p-4 mb-4 flex items-center gap-3">
                                    <ShieldCheckIcon className="h-8 w-8 text-emerald-400 flex-shrink-0" />
                                    <div className="text-left">
                                        <p className="text-emerald-300 font-semibold">
                                            Subscription set up successfully
                                        </p>
                                        <p className="text-emerald-400 text-sm">
                                            {apiMessage || 'Your subscription has been activated.'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {isGuest && (
                                <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4 mb-4">
                                    <p className="text-blue-300 text-sm">
                                        <strong>What happens next?</strong><br/>
                                        • You'll receive a confirmation email shortly<br/>
                                        • Payment processing may take a few minutes<br/>
                                        • Check your spam folder if you don't see the email
                                    </p>
                                </div>
                            )}

                            <div className="bg-emerald-900/20 border border-emerald-700 rounded-lg p-4">
                                <p className="text-emerald-300 text-sm">
                                    {isGuest
                                        ? 'Redirecting you back to classes in 10 seconds...'
                                        : outcome === OUTCOME.CLASS
                                            ? 'Redirecting to your classes in 5 seconds...'
                                            : outcome === OUTCOME.TOKENS
                                                ? 'Redirecting to your dashboard in 5 seconds...'
                                                : 'Redirecting to your dashboard in 3 seconds...'}
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                                <XCircleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2">Payment Processing Failed</h2>
                            <p className="text-gray-400 mb-6">
                                There was an issue processing your payment completion.
                            </p>
                            {error && (
                                <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
                                    <p className="text-red-300 text-sm">{error}</p>
                                </div>
                            )}
                        </>
                    )}
                    
                    {!isGuest ? (
                        outcome === OUTCOME.CLASS ? (
                            <button
                                onClick={() => navigate(venueKey ? `/classes/${venueKey}` : '/dashboard')}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-500 transition-colors"
                            >
                                View My Classes
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/dashboard/manage-subscription')}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                            >
                                Go to Subscription Management
                            </button>
                        )
                    ) : (
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={() => navigate('/classes')}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                            >
                                Browse More Classes
                            </button>
                            <button
                                onClick={() => navigate('/create-account')}
                                className="inline-flex items-center px-6 py-3 border border-gray-600 text-base font-medium rounded-lg text-gray-300 bg-transparent hover:bg-gray-800 transition-colors"
                            >
                                Create Full Account
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentComplete;
